import { motion } from "framer-motion";
import { ArrowRight, Phone } from "lucide-react";
import { SITE, IMAGES } from "../lib/site";

export default function Hero() {
  return (
    <section
      id="top"
      className="relative pt-32 pb-20 lg:pt-44 lg:pb-28 overflow-hidden"
      data-testid="hero-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
        {/* Left: Copy */}
        <div className="lg:col-span-7">
          <motion.span
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-block font-sans-body text-[11px] uppercase tracking-[0.3em] text-[#4A4A46] mb-8"
            data-testid="hero-eyebrow"
          >
            Locally Owned · Fort Worth, TX · Est. 2019
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-serif-display text-[clamp(2.75rem,7vw,6.5rem)] leading-[0.95] tracking-tightest text-[#1C1C1A]"
            data-testid="hero-heading"
          >
            Painting that
            <br />
            <span className="italic font-light">treats every wall</span>
            <br />
            like it's <span className="text-[#CC5500]">our own.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-10 max-w-xl font-sans-body text-base lg:text-lg text-[#4A4A46] leading-relaxed"
            data-testid="hero-subhead"
          >
            JSE Painting Services is a family-run shop in the Fort Worth area
            with over {SITE.yearsExperience} years of interior & exterior work
            — detail-obsessed, on time, and quietly proud of the trim lines we leave behind.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#estimate"
              data-testid="hero-cta-estimate"
              className="group inline-flex items-center gap-3 bg-[#1C1C1A] text-[#F5F5F0] hover:bg-[#CC5500] transition-colors duration-300 px-7 py-4 text-sm font-sans-body uppercase tracking-[0.2em]"
            >
              Get a Free Estimate
              <ArrowRight
                size={16}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </a>
            <a
              href={SITE.phoneHref}
              data-testid="hero-cta-call"
              className="inline-flex items-center gap-2 text-[#1C1C1A] hover:text-[#CC5500] transition-colors duration-200 px-2 py-2 text-sm font-sans-body uppercase tracking-[0.2em] border-b border-[#1C1C1A]"
            >
              <Phone size={15} />
              {SITE.phone}
            </a>
          </motion.div>
        </div>

        {/* Right: Image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.2, ease: "easeOut" }}
          className="lg:col-span-5 relative"
        >
          <div className="relative aspect-[3/4] w-full overflow-hidden">
            <img
              src={IMAGES.hero}
              alt="JSE painter in full PPE on a Fort Worth interior repaint"
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="hero-image"
            />
            <div className="absolute inset-0 bg-[#1C1C1A]/15 mix-blend-multiply" />
            <div className="absolute bottom-5 left-5 right-5 bg-[#F5F5F0]/95 backdrop-blur-sm px-5 py-4 border-l-2 border-[#CC5500]">
              <p className="font-serif-display text-xl italic text-[#1C1C1A] leading-snug">
                "Done right, the second time should never be needed."
              </p>
              <p className="mt-2 font-sans-body text-[10px] uppercase tracking-[0.25em] text-[#4A4A46]">
                — JSE crew motto
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Stats strip */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-20 lg:mt-28 border-t border-[#E5E5E0] pt-10 grid grid-cols-2 lg:grid-cols-4 gap-8">
        {[
          { k: "5+", v: "Years in business" },
          { k: "24/7", v: "Service availability" },
          { k: "100%", v: "Free estimates" },
          { k: "Local", v: "Fort Worth owned" },
        ].map((s, i) => (
          <motion.div
            key={s.v}
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 * i }}
            data-testid={`hero-stat-${i}`}
          >
            <div className="font-serif-display text-4xl lg:text-5xl text-[#1C1C1A] tracking-tighter">
              {s.k}
            </div>
            <div className="mt-1 font-sans-body text-xs uppercase tracking-[0.2em] text-[#4A4A46]">
              {s.v}
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
